import React, { useEffect } from "react";
import { Button, Grid, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import AppSwitch from "components/Shared/AppSwitch";
import { useLazyGetDDLRouteShipmentsQuery } from "store/services/routeShipmentApi";
import { useAuth } from "hooks/useAuth";

const DriverRouteAssignmentForm = ({
  assignment,
  onSubmit,
  onCancel,
  isSubmitting,
  driverId,
}) => {
  const { viewEntity } = useAuth();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      route: null,
      isPrimary: false,
      status: "active",
      notes: "",
      isActive: true,
    },
  });

  const [fetchRoutes, { data: routes, isLoading: loadingRoutes }] =
    useLazyGetDDLRouteShipmentsQuery();

  useEffect(() => {
    if (driverId && viewEntity?.entity?.account && viewEntity?.entity?._id) {
      fetchRoutes({
        account: viewEntity.entity.account?._id,
        entity: viewEntity.entity._id,
        isActive: true,
      });
    }
  }, [fetchRoutes, driverId, viewEntity]);

  useEffect(() => {
    if (assignment) {
      reset({
        route: assignment.route || null,
        isPrimary: assignment.isPrimary || false,
        status: assignment.status || "active",
        notes: assignment.notes || "",
        isActive: assignment.isActive ?? true,
      });
    }
  }, [assignment, reset]);

  const handleOnSubmit = (data) => {
    const formattedData = {
      ...data,
      route: data.route?._id,
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    };
    onSubmit(formattedData);
  };

  return (
    <AppForm loading={isSubmitting}>
      <AppForm.Content>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Controller
              name="route"
              control={control}
              rules={{ required: "Please select a route." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Route"
                  required
                  value={value ?? null}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) => option?.routeName || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={routes || []}
                  loading={loadingRoutes}
                  loadingText="Loading routes..."
                  helperText={errors?.route?.message}
                  error={!!errors?.route}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="isPrimary"
              control={control}
              render={({ field }) => (
                <AppSwitch
                  {...field}
                  label="Primary Route"
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="status"
              control={control}
              rules={{ required: "Please select a status." }}
              render={({ field }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Status"
                  required
                  value={field.value}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={(option) => option || ""}
                  isOptionEqualToValue={(option, value) => option === value}
                  options={["active", "inactive"]}
                  disabled={isSubmitting}
                  helperText={errors?.status?.message}
                  error={!!errors?.status}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Notes"
                  multiline
                  rows={4}
                  disabled={isSubmitting}
                  helperText={errors?.notes?.message}
                  error={!!errors?.notes}
                />
              )}
            />
          </Grid>
          {assignment && (
            <Grid item xs={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch
                    {...field}
                    label="Active Status"
                    disabled={isSubmitting}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnSubmit)}
          disabled={isSubmitting}
        >
          {assignment ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default DriverRouteAssignmentForm;
